import React from 'react';
import PropTypes from 'prop-types';
import { Box, CircularProgress, Typography, Paper } from '@mui/material';

/**
 * Componente reutilizable para mostrar estados de carga
 * 
 * @param {Object} props - Propiedades del componente
 * @param {string} props.message - Mensaje a mostrar durante la carga
 * @param {string} props.size - Tamaño del indicador de carga ('small', 'medium', 'large')
 * @param {boolean} props.fullScreen - Si debe cubrir toda la pantalla o no
 * @param {boolean} props.withBackground - Si debe mostrar un fondo con papel y sombra
 * @param {string} props.color - Color del indicador de carga ('primary', 'secondary', etc.)
 * @param {number} props.minHeight - Altura mínima del contenedor
 */
const Loader = ({ 
  message, 
  size = 'medium', 
  fullScreen = false, 
  withBackground = false,
  color = 'primary',
  minHeight = 200
}) => {
  // Mapear tamaños a valores numéricos para CircularProgress
  const sizeMap = {
    small: 24,
    medium: 40,
    large: 60
  };

  // Contenido del loader
  const loaderContent = (
    <>
      <CircularProgress 
        size={sizeMap[size]} 
        color={color} 
        thickness={4}
      />
      {message && (
        <Typography 
          variant={size === 'small' ? 'body2' : 'body1'} 
          color="textSecondary" 
          align="center"
          sx={{ mt: 2 }}
        >
          {message}
        </Typography>
      )}
    </>
  );

  // Si debe cubrir toda la pantalla
  if (fullScreen) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          zIndex: 9999,
          backgroundColor: 'rgba(255, 255, 255, 0.8)',
          backdropFilter: 'blur(4px)'
        }}
      >
        {loaderContent}
      </Box>
    );
  }

  // Con fondo de papel (para componentes que necesitan destacar el área de carga)
  if (withBackground) {
    return (
      <Paper
        elevation={2}
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          p: 3,
          minHeight,
          width: '100%'
        }}
      >
        {loaderContent}
      </Paper>
    );
  }

  // Loader básico
  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight,
        width: '100%'
      }}
    >
      {loaderContent}
    </Box>
  );
};

Loader.propTypes = {
  message: PropTypes.string,
  size: PropTypes.oneOf(['small', 'medium', 'large']),
  fullScreen: PropTypes.bool,
  withBackground: PropTypes.bool,
  color: PropTypes.string,
  minHeight: PropTypes.number
};

export default Loader;